// Auto-key detection — given an AudioBuffer (e.g. an uploaded beat), estimate
// the musical key and mode using chromagram + Krumhansl-Schmuckler profiles.
//
// Approach:
//   1. Slice the buffer into ~93ms frames, FFT each frame, fold spectrum into 12
//      pitch classes (chromagram).
//   2. Average chroma across the buffer to a single 12-bin pitch class profile.
//   3. Correlate against the 12 rotations of the Krumhansl-Kessler major + minor
//      key profiles. Pick the highest correlation.

const NOTE_NAMES = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];

// Krumhansl-Kessler key profiles (relative weights for each pitch class given a key).
const KK_MAJOR = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const KK_MINOR = [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

const pearson = (a, b) => {
  const n = a.length;
  let sa = 0, sb = 0, saSq = 0, sbSq = 0, sab = 0;
  for (let i = 0; i < n; i++) {
    sa += a[i]; sb += b[i];
    saSq += a[i] * a[i]; sbSq += b[i] * b[i];
    sab += a[i] * b[i];
  }
  const num = sab - (sa * sb) / n;
  const den = Math.sqrt((saSq - (sa * sa) / n) * (sbSq - (sb * sb) / n));
  return den === 0 ? 0 : num / den;
};

const rotate = (arr, n) => arr.slice(n).concat(arr.slice(0, n));

// Compute a chroma vector from a single frame using simple bin-folding.
function frameChroma(magnitudes, sampleRate, fftSize) {
  const chroma = new Float32Array(12);
  const halfSize = magnitudes.length;
  const aref = 440; // A4
  for (let k = 1; k < halfSize; k++) {
    const freq = (k * sampleRate) / fftSize;
    if (freq < 80 || freq > 5000) continue; // ignore sub-bass and very high
    const midi = 69 + 12 * Math.log2(freq / aref);
    const pc = ((Math.round(midi) % 12) + 12) % 12;
    chroma[pc] += magnitudes[k];
  }
  // normalize
  let max = 0;
  for (let i = 0; i < 12; i++) if (chroma[i] > max) max = chroma[i];
  if (max > 0) for (let i = 0; i < 12; i++) chroma[i] /= max;
  return chroma;
}

// Iterative in-place radix-2 Cooley-Tukey FFT (no deps).
function fftMagnitudes(samples, fftSize) {
  // Pad/truncate to fftSize
  const re = new Float32Array(fftSize);
  const im = new Float32Array(fftSize);
  for (let i = 0; i < fftSize; i++) re[i] = samples[i] || 0;

  // bit-reverse
  const n = fftSize;
  let j = 0;
  for (let i = 1; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = re[i]; re[i] = re[j]; re[j] = tr;
      const ti = im[i]; im[i] = im[j]; im[j] = ti;
    }
  }
  // butterflies
  for (let len = 2; len <= n; len <<= 1) {
    const ang = -2 * Math.PI / len;
    const wlr = Math.cos(ang), wli = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let wr = 1, wi = 0;
      for (let k = 0; k < len / 2; k++) {
        const ur = re[i + k], ui = im[i + k];
        const vr = re[i + k + len / 2] * wr - im[i + k + len / 2] * wi;
        const vi = re[i + k + len / 2] * wi + im[i + k + len / 2] * wr;
        re[i + k] = ur + vr; im[i + k] = ui + vi;
        re[i + k + len / 2] = ur - vr; im[i + k + len / 2] = ui - vi;
        const newWr = wr * wlr - wi * wli;
        wi = wr * wli + wi * wlr;
        wr = newWr;
      }
    }
  }
  const mags = new Float32Array(n / 2);
  for (let i = 0; i < n / 2; i++) mags[i] = Math.sqrt(re[i] * re[i] + im[i] * im[i]);
  return mags;
}

export async function detectKeyFromAudioBuffer(audioBuffer, options = {}) {
  const { fftSize = 4096, hopSize = 2048, maxDurationSeconds = 60 } = options;
  const sampleRate = audioBuffer.sampleRate;
  const ch0 = audioBuffer.getChannelData(0);
  const sampleLimit = Math.min(ch0.length, Math.floor(maxDurationSeconds * sampleRate));

  // Average chroma across frames
  const sumChroma = new Float32Array(12);
  let frames = 0;
  for (let start = 0; start + fftSize <= sampleLimit; start += hopSize) {
    const slice = ch0.subarray(start, start + fftSize);
    // Apply Hann window
    const windowed = new Float32Array(fftSize);
    for (let i = 0; i < fftSize; i++) {
      const w = 0.5 * (1 - Math.cos(2 * Math.PI * i / (fftSize - 1)));
      windowed[i] = slice[i] * w;
    }
    const mags = fftMagnitudes(windowed, fftSize);
    const c = frameChroma(mags, sampleRate, fftSize);
    for (let i = 0; i < 12; i++) sumChroma[i] += c[i];
    frames++;
  }
  if (frames === 0) return { key: "C", mode: "major", confidence: 0 };
  const avgChroma = Array.from(sumChroma, v => v / frames);

  // Correlate against all 24 keys
  let bestKey = 0, bestMode = "major", bestCorr = -Infinity;
  for (let i = 0; i < 12; i++) {
    const majorRot = rotate(KK_MAJOR, (12 - i) % 12);
    const minorRot = rotate(KK_MINOR, (12 - i) % 12);
    const majC = pearson(avgChroma, majorRot);
    const minC = pearson(avgChroma, minorRot);
    if (majC > bestCorr) { bestCorr = majC; bestKey = i; bestMode = "major"; }
    if (minC > bestCorr) { bestCorr = minC; bestKey = i; bestMode = "minor"; }
  }
  return {
    key: NOTE_NAMES[bestKey],
    mode: bestMode,
    confidence: parseFloat(bestCorr.toFixed(3)),
    chroma: avgChroma,
  };
}

// Helper for the autotune worklet: given a detected key + mode, return the
// scale degree note names (with the detected root as the first element).
export function scaleNotesForKey(rootName, mode) {
  const intervals = mode === "minor" ? [0, 2, 3, 5, 7, 8, 10] : [0, 2, 4, 5, 7, 9, 11];
  const rootIdx = NOTE_NAMES.indexOf(rootName);
  if (rootIdx < 0) return ["C","D","E","F","G","A","B"];
  return intervals.map(i => NOTE_NAMES[(rootIdx + i) % 12]);
}

export { NOTE_NAMES };
